import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import TeamList from '../components/TeamList';
import RecentRetrospectives from '../components/RecentRetrospectives';
import ActionItemSummary from '../components/ActionItemSummary';
import MetricCard from '../components/MetricCard';
import { Button } from '../components/ui/button';
import { Play, Users, BarChart2, Settings, MessageSquare, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import UserTierBadge from '../components/UserTierBadge';
import { trackEvent, ANALYTICS_EVENTS } from '../services/analytics';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalRetros: 0,
    openActions: 0,
    totalTeams: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [retrosRes, actionsRes, teamsRes] = await Promise.all([
          api.get('/retrospectives?limit=1'), // Just get count from meta if possible, or list length
          api.get('/action-items'),
          api.get('/teams?limit=1')
        ]);

        const retrosData = retrosRes.data?.data?.data || (Array.isArray(retrosRes.data?.data) ? retrosRes.data?.data : []);
        const actionsData = actionsRes.data?.data?.data || (Array.isArray(actionsRes.data?.data) ? actionsRes.data?.data : []);
        const teamsData = teamsRes.data?.data?.data || (Array.isArray(teamsRes.data?.data) ? teamsRes.data?.data : []); // Handle pagination structure

        // Note: For a real app with pagination, we'd want a stats endpoint. 
        // Here we rely on what we can get. If pagination limits results, this might be partial data 
        // unless we use the 'meta.total' if available. 
        // Assuming /action-items returns all for now or we just count what we get.
        // For teams and retros, we'll try to use meta if available, otherwise length is a fallback approximation.
        
        const totalRetros = retrosRes.data?.data?.meta?.total || retrosData.length;
        const totalTeams = teamsRes.data?.data?.meta?.total || teamsData.length;
        
        const openActionsCount = actionsData.filter(
          (item: any) => item.status === 'open' || item.status === 'in-progress'
        ).length;

        setStats({
          totalRetros,
          openActions: openActionsCount,
          totalTeams
        });

      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      }
    };

    fetchStats();
  }, []);

  return (
    <Layout showBreadcrumb={false}>
      <div className="w-full space-y-8 animate-fade-in-up">
        {/* Welcome Section */}
        <div className="relative overflow-hidden bg-[#BBE0EF] p-4 md:p-10 border-4 border-[#161E54] shadow-neo-lg">
           <div className="absolute top-0 right-0 p-4 opacity-10">
               <svg width="200" height="200" viewBox="0 0 100 100" fill="#161E54" xmlns="http://www.w3.org/2000/svg">
                   <rect x="10" y="10" width="30" height="30" />
                   <rect x="50" y="50" width="30" height="30" />
                   <circle cx="70" cy="20" r="15" />
                   <circle cx="20" cy="70" r="15" />
               </svg>
           </div>
          
          <div className="relative z-10">
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-[#161E54] uppercase mb-2">
              HELLO, {user?.name?.split(' ')[0]}! <span className="inline-block hover:animate-spin">👋</span>
            </h1>
            <div className="flex items-center gap-4">
              <p className="text-[#161E54] text-lg font-bold font-mono opacity-80">
                READY TO RETRO?
              </p>
            </div>
          </div>
        </div>

        {/* High-Level Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard 
            title="My Teams" 
            value={stats.totalTeams} 
            icon={Users} 
            colorScheme="blue"
          />
          <MetricCard 
            title="Active Retros" 
            value={stats.totalRetros} 
            icon={MessageSquare} 
            colorScheme="purple"
          />
          <MetricCard 
            title="Pending Actions" 
            value={stats.openActions} 
            icon={CheckCircle2} 
            colorScheme={stats.openActions > 0 ? "amber" : "green"}
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            className="h-24 flex flex-col items-center justify-center gap-2 bg-[#F16D34] text-white border-4 border-[#161E54] shadow-neo hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-200 font-bold text-lg rounded-none uppercase tracking-wide whitespace-normal text-center p-2"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.CREATE_RETRO, { source: 'dashboard_quick_action' });
              navigate('/retrospectives/create');
            }}
          >
            <Play className="w-8 h-8 fill-current" />
            <span className="text-sm md:text-base">Start Retro</span>
          </Button>
          
          <Button 
            variant="outline" 
            className="h-24 flex flex-col items-center justify-center gap-2 bg-white text-[#161E54] border-4 border-[#161E54] shadow-neo hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-200 font-bold text-lg rounded-none uppercase tracking-wide whitespace-normal text-center p-2"
            onClick={() => {
              trackEvent(ANALYTICS_EVENTS.CREATE_TEAM, { source: 'dashboard_quick_action' });
              navigate('/teams/create');
            }}
          >
            <Users className="w-8 h-8" />
            <span className="text-sm md:text-base">Create Team</span>
          </Button>

          <Button 
            variant="outline" 
            className="h-24 flex flex-col items-center justify-center gap-2 bg-white text-[#161E54] border-4 border-[#161E54] shadow-neo hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-200 font-bold text-lg rounded-none uppercase tracking-wide whitespace-normal text-center p-2"
            onClick={() => navigate('/analytics')}
          >
            <BarChart2 className="w-8 h-8" />
            <span className="text-sm md:text-base">Analytics</span>
          </Button>

          <Button 
            variant="outline" 
            className="h-24 flex flex-col items-center justify-center gap-2 bg-white text-[#161E54] border-4 border-[#161E54] shadow-neo hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-200 font-bold text-lg rounded-none uppercase tracking-wide whitespace-normal text-center p-2"
            onClick={() => navigate('/profile')}
          >
            <Settings className="w-8 h-8" />
            <span className="text-sm md:text-base">Settings</span>
          </Button>
        </div>

          {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - 2/3 width */}
          <div className="lg:col-span-2 space-y-8">
            <section className="animate-fade-in-up animate-stagger-1">
              <RecentRetrospectives limit={3} />
            </section>

            <section className="animate-fade-in-up animate-stagger-2">
              <TeamList />
            </section>
          </div>

          {/* Right Column - 1/3 width */}
          <div className="lg:col-span-1 space-y-8">
            <section className="animate-fade-in-up animate-stagger-3 h-full">
              <ActionItemSummary />
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
